'use client';

import { RadioGroup as ChakraRadioGroup  } from '@chakra-ui/react';
import type { RadioGroupRootProps } from '@chakra-ui/react';
import { Controller, useFormContext } from 'react-hook-form';

import type { FormBaseInputProps } from '../../../types';
import { FormField } from '../../wrapper';

export type Option = {
	label: string
	value: string
};

export type RadioGroupProps<T> = FormBaseInputProps<T> & {
	direction?: 'horizontal' | 'vertical'
	gap?:       RadioGroupRootProps['gap']
	options:    Option[]
};

export const RadioGroup = <T extends Record<string, unknown>>({
	direction = 'vertical',
	gap = 4,
	helperText,
	label,
	name,
	optionalText,
	options,
	required,
}: RadioGroupProps<T>) => {
	const { control } = useFormContext<T>();
	const props = {
		display:       'flex',
		flexDirection: direction === 'horizontal' ? 'row' : 'column',
		gap,
	};

	return (
		<FormField
			helperText={helperText}
			label={label}
			name={name}
			optionalText={optionalText}
			required={required}
		>
			<Controller
				control={control}
				name={name}
				render={({ field }) => (
					<ChakraRadioGroup.Root
						name={field.name}
						onValueChange={({ value }) => field.onChange(value)}
						value={field.value as null | string | undefined}
						{...props}
					>
						{options.map(option => (
							<ChakraRadioGroup.Item key={option.value} value={option.value}>
								<ChakraRadioGroup.ItemHiddenInput onBlur={field.onBlur} />
								<ChakraRadioGroup.ItemIndicator />
								<ChakraRadioGroup.ItemText>
									{option.label}
								</ChakraRadioGroup.ItemText>
							</ChakraRadioGroup.Item>
						))}
					</ChakraRadioGroup.Root>
				)}
			/>
		</FormField>
	);
};
